import React from 'react'
import { Box, Chip, Paper, Stack, Typography } from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import {
  ACTIVITY_REPORT_MAP_COLORS,
  ACTIVITY_REPORT_MAP_SX,
  ACTIVITY_REPORT_MAP_TEXT
} from './constants'

function MobileInfoRow({ icon, label, value }) {
  return (
    <Box className="activity-map-mobile-info-row">
      <Box className="activity-map-mobile-info-icon">{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography className="activity-map-mobile-info-label">{label}</Typography>
        <Typography className="activity-map-mobile-info-value">{value ?? '-'}</Typography>
      </Box>
    </Box>
  )
}

export default function ActivityReportMapDetails({
  clientName,
  activityName,
  dateTime,
  effectiveLocation,
  overline = ACTIVITY_REPORT_MAP_TEXT.detailsSectionTitle,
  items,
  customGeofenceDistance,
  geofenceChipLabel,
  isMobileLayout = false,
  compactTypography = false
}) {
  const fontSize = (size) => size - (compactTypography ? 1 : 0)
  const detailItems = items ?? [
    {
      key: 'activity',
      icon: <SellOutlinedIcon fontSize="small" />,
      label: ACTIVITY_REPORT_MAP_TEXT.activityLabel,
      value: activityName || ACTIVITY_REPORT_MAP_TEXT.emptyValue
    },
    {
      key: 'dateTime',
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
      label: ACTIVITY_REPORT_MAP_TEXT.dateTimeLabel,
      value: dateTime || ACTIVITY_REPORT_MAP_TEXT.emptyValue
    },
    {
      key: 'effectiveLocation',
      icon: <PlaceOutlinedIcon fontSize="small" />,
      label: ACTIVITY_REPORT_MAP_TEXT.geofenceStatusLabel,
      value: effectiveLocation || ACTIVITY_REPORT_MAP_TEXT.emptyValue
    }
  ]
  const geofenceLabel =
    geofenceChipLabel ||
    (customGeofenceDistance
      ? ACTIVITY_REPORT_MAP_TEXT.customGeofenceLabel(customGeofenceDistance)
      : null)
  const geofenceChip = geofenceLabel ? (
    <Chip
      icon={<PlaceOutlinedIcon fontSize="small" />}
      label={geofenceLabel}
      variant="outlined"
      sx={ACTIVITY_REPORT_MAP_SX.geofenceChip}
    />
  ) : null

  if (isMobileLayout) {
    return (
      <>
        <Box className="activity-map-mobile-info-card">
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: ACTIVITY_REPORT_MAP_COLORS.mobileOverline,
                fontSize: fontSize(11),
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}
            >
              {overline}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: ACTIVITY_REPORT_MAP_COLORS.mobileTitle,
                fontSize: fontSize(20),
                fontWeight: 800,
                lineHeight: 1.25,
                wordBreak: 'break-word'
              }}
            >
              {clientName || ACTIVITY_REPORT_MAP_TEXT.emptyValue}
            </Typography>
          </Box>
          {geofenceChip}
        </Box>

        <Box className="activity-map-mobile-info-card">
          {detailItems.map((item) => (
            <MobileInfoRow
              key={item.key}
              icon={item.icon}
              label={item.label}
              value={item.value ?? ACTIVITY_REPORT_MAP_TEXT.emptyValue}
            />
          ))}
        </Box>
      </>
    )
  }

  return (
    <Paper
      className="activity-report-details"
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${ACTIVITY_REPORT_MAP_COLORS.desktopCardBorder}`,
        background: ACTIVITY_REPORT_MAP_COLORS.desktopGradient,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderBottom: `1px solid ${ACTIVITY_REPORT_MAP_COLORS.desktopCardBottomBorder}`
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: ACTIVITY_REPORT_MAP_COLORS.desktopOverline,
                fontWeight: 700,
                letterSpacing: '0.08em',
                lineHeight: 1.2
              }}
            >
              {overline}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: fontSize(22),
                fontWeight: 700,
                lineHeight: 1.2
              }}
            >
              {clientName || ACTIVITY_REPORT_MAP_TEXT.emptyValue}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {geofenceChip}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: `repeat(${Math.min(detailItems.length || 1, 4)}, minmax(0, 1fr))`
            },
            gap: 1.5,
            width: '100%'
          }}
        >
          {detailItems.map((item) => (
            <Box
              className="activity-report-details__metric"
              key={item.key}
              sx={ACTIVITY_REPORT_MAP_SX.desktopDetailItem}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={ACTIVITY_REPORT_MAP_SX.desktopDetailIcon}>{item.icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: ACTIVITY_REPORT_MAP_COLORS.desktopDetailLabel,
                      fontSize: fontSize(12),
                      fontWeight: 600,
                      lineHeight: 1.2
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      mt: 0.25,
                      color: ACTIVITY_REPORT_MAP_COLORS.desktopDetailValue,
                      fontSize: fontSize(15),
                      fontWeight: 700,
                      lineHeight: 1.35,
                      wordBreak: 'break-word'
                    }}
                  >
                    {item.value ?? ACTIVITY_REPORT_MAP_TEXT.emptyValue}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  )
}
